import React from "react";
import { Formik } from "formik";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import AppModal from "../../../components/AppModal";
import AppButton from "../../../shared/AppButton";
import CustomFilterInput from "../../../shared/CustomFilterInput";
import { saveNHICSettings } from "../nhicApi";

const getConfigByType = (editData, type) =>
  editData?.configs?.find((config) => config.type === type) || {};

const Section = ({ title, children }) => (
  <div className="rounded-md border border-slate-200 bg-white p-3">
    <div className="mb-2 flex items-center justify-between border-b border-slate-100 pb-2">
      <h3 className="text-[13px] font-bold text-slate-700">{title}</h3>
    </div>
    <div className="grid grid-cols-1 gap-2 md:grid-cols-2">{children}</div>
  </div>
);

function NHICSettingsModal({ isOpen, onClose, editData, refetch }) {
  const { t } = useTranslation();
  const [loading, setLoading] = React.useState(false);
  const patientConfig = getConfigByType(editData, "patient");
  const practitionerConfig = getConfigByType(editData, "practitioner");

  const initialValues = {
    auth_base_url: editData?.auth_base_url || "",
    patient_base_url: patientConfig.base_url || "",
    patient_consumer_key: patientConfig.consumer_key || "",
    patient_consumer_secret: "",
    patient_developer_email: patientConfig.developer_email || "",
    patient_developer_email_branch:
      patientConfig.developer_email_branch || "",
    practitioner_base_url: practitionerConfig.base_url || "",
    practitioner_consumer_key: practitionerConfig.consumer_key || "",
    practitioner_consumer_secret: "",
    practitioner_developer_email: practitionerConfig.developer_email || "",
    practitioner_developer_email_branch:
      practitionerConfig.developer_email_branch || "",
  };

  const validate = (values) => {
    const errors = {};

    if (!values.auth_base_url) {
      errors.auth_base_url = t("Auth base URL is required");
    }
    if (!values.patient_base_url) {
      errors.patient_base_url = t("Patient base URL is required");
    }
    if (!values.patient_consumer_key) {
      errors.patient_consumer_key = t("Patient consumer key is required");
    }
    if (!editData && !values.patient_consumer_secret) {
      errors.patient_consumer_secret = t(
        "Patient consumer secret is required for initial setup",
      );
    }
    if (!values.patient_developer_email) {
      errors.patient_developer_email = t("Developer Email is required");
    }
    if (!values.practitioner_base_url) {
      errors.practitioner_base_url = t("Practitioner base URL is required");
    }
    if (!values.practitioner_consumer_key) {
      errors.practitioner_consumer_key = t(
        "Practitioner consumer key is required",
      );
    }
    if (!editData && !values.practitioner_consumer_secret) {
      errors.practitioner_consumer_secret = t(
        "Practitioner consumer secret is required for initial setup",
      );
    }

    return errors;
  };

  const buildConfig = (type, values) => {
    const prefix = type === "patient" ? "patient" : "practitioner";
    const config = {
      type,
      base_url: values[`${prefix}_base_url`],
      consumer_key: values[`${prefix}_consumer_key`],
      developer_email: values[`${prefix}_developer_email`],
      developer_email_branch: values[`${prefix}_developer_email_branch`],
    };

    if (values[`${prefix}_consumer_secret`]) {
      config.consumer_secret = values[`${prefix}_consumer_secret`];
    }

    return config;
  };

  const handleSubmit = async (values) => {
    const input = {
      auth_base_url: values.auth_base_url,
      configs: [
        buildConfig("patient", values),
        buildConfig("practitioner", values),
      ],
    };

    try {
      setLoading(true);
      const response = await saveNHICSettings(input);
      if (response.status === 200) {
        toast.success(response.message || t("Settings saved successfully"));
        refetch && refetch();
        onClose();
      } else {
        toast.error(response.message || t("Failed to save settings"));
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.error ||
          error?.response?.data?.message ||
          error.message ||
          t("An error occurred while saving settings"),
      );
    } finally {
      setLoading(false);
    }
  };

  const renderSecretPlaceholder = (configured) =>
    configured ? t("Leave blank to keep existing") : t("Consumer Secret");

  return (
    <AppModal
      open={isOpen}
      onClose={onClose}
      title={editData ? t("Update NHIC Settings") : t("Configure NHIC Settings")}
      maxWidth="md"
      contentSx={{ backgroundColor: "#f8fafc" }}
    >
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validate={validate}
        onSubmit={handleSubmit}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
        }) => (
          <form onSubmit={handleSubmit} className="space-y-3">
            <Section title={t("Authentication")}>
              <div className="md:col-span-2">
                <CustomFilterInput
                  label={`${t("Authentication Base URL")} *`}
                  name="auth_base_url"
                  value={values.auth_base_url}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="https://services-test.nhic.gov.sa"
                  error={errors.auth_base_url}
                  touched={touched.auth_base_url}
                />
              </div>
            </Section>

            <Section title={t("Patient Registry Configuration")}>
              <div className="md:col-span-2">
                <CustomFilterInput
                  label={`${t("Base URL")} *`}
                  name="patient_base_url"
                  value={values.patient_base_url}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="https://api.leanstg.io/patients-registry"
                  error={errors.patient_base_url}
                  touched={touched.patient_base_url}
                />
              </div>
              <CustomFilterInput
                label={`${t("Consumer Key")} *`}
                name="patient_consumer_key"
                value={values.patient_consumer_key}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder={t("Consumer Key")}
                error={errors.patient_consumer_key}
                touched={touched.patient_consumer_key}
              />
              <CustomFilterInput
                label={`${t("Consumer Secret")}${editData ? "" : " *"}`}
                name="patient_consumer_secret"
                type="password"
                value={values.patient_consumer_secret}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder={renderSecretPlaceholder(
                  patientConfig.consumer_secret_exists,
                )}
                error={errors.patient_consumer_secret}
                touched={touched.patient_consumer_secret}
              />
              <CustomFilterInput
                label={`${t("Developer Email")} *`}
                name="patient_developer_email"
                type="email"
                value={values.patient_developer_email}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="developer@example.com"
                error={errors.patient_developer_email}
                touched={touched.patient_developer_email}
              />
              <CustomFilterInput
                label={t("Developer Email Branch")}
                name="patient_developer_email_branch"
                value={values.patient_developer_email_branch}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder={t("Branch")}
              />
            </Section>

            <Section title={t("Practitioner Registry Configuration")}>
              <div className="md:col-span-2">
                <CustomFilterInput
                  label={`${t("Base URL")} *`}
                  name="practitioner_base_url"
                  value={values.practitioner_base_url}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="https://services-test.nhic.gov.sa/practitioners-registry"
                  error={errors.practitioner_base_url}
                  touched={touched.practitioner_base_url}
                />
              </div>
              <CustomFilterInput
                label={`${t("Consumer Key")} *`}
                name="practitioner_consumer_key"
                value={values.practitioner_consumer_key}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder={t("Consumer Key")}
                error={errors.practitioner_consumer_key}
                touched={touched.practitioner_consumer_key}
              />
              <CustomFilterInput
                label={`${t("Consumer Secret")}${editData ? "" : " *"}`}
                name="practitioner_consumer_secret"
                type="password"
                value={values.practitioner_consumer_secret}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder={renderSecretPlaceholder(
                  practitionerConfig.consumer_secret_exists,
                )}
                error={errors.practitioner_consumer_secret}
                touched={touched.practitioner_consumer_secret}
              />
              <CustomFilterInput
                label={t("Developer Email")}
                name="practitioner_developer_email"
                type="email"
                value={values.practitioner_developer_email}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="developer@example.com"
              />
              <CustomFilterInput
                label={t("Developer Email Branch")}
                name="practitioner_developer_email_branch"
                value={values.practitioner_developer_email_branch}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder={t("Branch")}
              />
            </Section>

            <div className="flex justify-end gap-2 border-t border-slate-200 pt-3">
              <AppButton
                label={t("Cancel")}
                variant="outlined"
                onClick={onClose}
                disabled={loading}
                className="!border-gray-300 !text-gray-600 hover:!bg-gray-100"
              />
              <AppButton
                label={t("Save Settings")}
                type="submit"
                isLoading={loading}
                loadingText={t("Saving")}
              />
            </div>
          </form>
        )}
      </Formik>
    </AppModal>
  );
}

export default NHICSettingsModal;
